import { StreamerConnection } from '../lib/webrtc/streamer';
import { getMediaStream, FacingMode } from '../lib/media/capture';

type ConnectionStatus = 'connecting' | 'streaming' | 'reconnecting' | 'disconnected' | 'error';

export async function renderStream(container: HTMLElement, params?: Record<string, string>): Promise<void> {
  const rawEventId = params?.['$1'];
  if (!rawEventId) {
    container.innerHTML = '<div class="container"><h1>Invalid stream link</h1></div>';
    return;
  }
  const eventId: string = rawEventId;

  container.innerHTML = `
    <div class="stream-page">
      <div class="stream-container">
        <video id="preview" autoplay muted playsinline></video>
        <div class="stream-overlay">
          <div class="status-badge" id="status-badge">
            <span class="status-dot"></span>
            <span class="status-text">Loading camera...</span>
          </div>
        </div>

        <!-- Setup controls (before streaming) -->
        <div class="setup-controls" id="setup-controls" style="display: none;">
          <button class="control-btn" id="flip-camera-setup" title="Flip Camera">🔄</button>
          <button class="primary-btn" id="start-streaming-btn">Start Streaming</button>
        </div>

        <!-- Streaming controls (during stream) -->
        <div class="stream-controls" id="stream-controls" style="display: none;">
          <button class="control-btn" id="flip-camera" title="Flip Camera">🔄</button>
          <button class="control-btn" id="camera-toggle" title="Toggle Camera">📹</button>
          <button class="control-btn" id="mic-toggle" title="Toggle Microphone">🎤</button>
          <button class="control-btn danger" id="stop-btn" title="Stop Streaming">⏹</button>
        </div>
      </div>

      <div class="error-message" id="error-message" style="display: none;">
        <h2>Unable to Access Camera</h2>
        <p id="error-text"></p>
        <button class="primary" id="retry-btn">Try Again</button>
      </div>
    </div>

    <style>
      .stream-page {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 1rem;
        background: #000;
      }

      .stream-container {
        position: relative;
        width: 100%;
        max-width: 800px;
        aspect-ratio: 16 / 9;
        background: var(--color-surface);
        border-radius: var(--radius-lg);
        overflow: hidden;
      }

      #preview {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transform: scaleX(-1);
      }

      .stream-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        padding: 1rem;
        background: linear-gradient(rgba(0, 0, 0, 0.6), transparent);
      }

      .status-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        background: rgba(0, 0, 0, 0.6);
        border-radius: 9999px;
        font-size: 0.875rem;
        font-weight: 500;
      }

      .status-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(--color-warning);
      }

      .status-badge.streaming .status-dot {
        background: var(--color-success);
        animation: pulse 2s infinite;
      }

      .status-badge.reconnecting .status-dot {
        background: var(--color-warning);
        animation: pulse 1s infinite;
      }

      .status-badge.disconnected .status-dot,
      .status-badge.error .status-dot {
        background: var(--color-error);
      }

      .status-badge.ready .status-dot {
        background: #3b82f6;
      }

      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }

      .setup-controls {
        position: absolute;
        bottom: 1rem;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        gap: 0.75rem;
        align-items: center;
        padding: 0.75rem;
        background: rgba(0, 0, 0, 0.8);
        border-radius: var(--radius-lg);
      }

      .stream-controls {
        position: absolute;
        bottom: 1rem;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        gap: 0.5rem;
        padding: 0.5rem;
        background: rgba(0, 0, 0, 0.6);
        border-radius: var(--radius-lg);
      }

      .control-btn {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.1);
        border: none;
        cursor: pointer;
        font-size: 1.25rem;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
      }

      .control-btn:hover {
        background: rgba(255, 255, 255, 0.2);
      }

      .control-btn.danger {
        background: var(--color-error);
      }

      .control-btn.danger:hover {
        background: #dc2626;
      }

      .control-btn.disabled {
        opacity: 0.5;
      }

      .primary-btn {
        padding: 0.75rem 2rem;
        background: var(--color-primary);
        color: white;
        border: none;
        border-radius: var(--radius-lg);
        font-weight: 600;
        cursor: pointer;
        transition: background 0.2s;
      }

      .primary-btn:hover {
        background: #2563eb;
      }

      .error-message {
        text-align: center;
        margin-top: 2rem;
      }

      .error-message h2 {
        margin-bottom: 0.5rem;
      }

      .error-message p {
        color: var(--color-text-muted);
        margin-bottom: 1.5rem;
      }
    </style>
  `;

  const preview = document.getElementById('preview') as HTMLVideoElement;
  const statusBadge = document.getElementById('status-badge')!;
  const statusText = statusBadge.querySelector('.status-text')!;
  const setupControls = document.getElementById('setup-controls')!;
  const streamControls = document.getElementById('stream-controls')!;
  const errorMessage = document.getElementById('error-message')!;
  const errorText = document.getElementById('error-text')!;
  const startStreamingBtn = document.getElementById('start-streaming-btn')!;
  const flipCameraSetupBtn = document.getElementById('flip-camera-setup')!;
  const retryBtn = document.getElementById('retry-btn')!;
  const cameraToggle = document.getElementById('camera-toggle')!;
  const micToggle = document.getElementById('mic-toggle')!;
  const flipCameraBtn = document.getElementById('flip-camera')!;
  const stopBtn = document.getElementById('stop-btn')!;

  let mediaStream: MediaStream | null = null;
  let streamer: StreamerConnection | null = null;
  let cameraEnabled = true;
  let micEnabled = true;
  let facingMode: FacingMode = 'user';
  let isStreaming = false;

  function updateStatus(status: ConnectionStatus | 'ready'): void {
    statusBadge.className = `status-badge ${status}`;
    const statusMessages: Record<ConnectionStatus | 'ready', string> = {
      ready: 'Ready',
      connecting: 'Connecting...',
      streaming: 'Live',
      reconnecting: 'Reconnecting...',
      disconnected: 'Disconnected',
      error: 'Error',
    };
    statusText.textContent = statusMessages[status];
  }

  function showError(message: string): void {
    setupControls.style.display = 'none';
    streamControls.style.display = 'none';
    errorMessage.style.display = 'block';
    errorText.textContent = message;
    updateStatus('error');
  }

  async function initializeCamera(): Promise<void> {
    try {
      mediaStream = await getMediaStream(facingMode);
      preview.srcObject = mediaStream;

      updateStatus('ready');
      setupControls.style.display = 'flex';
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        showError('Camera permission was denied. Please allow camera access and refresh the page.');
      } else if (error.name === 'NotFoundError') {
        showError('No camera found. Please connect a camera and try again.');
      } else {
        showError(error.message || 'Failed to access camera');
      }
    }
  }

  async function flipCamera(): Promise<void> {
    if (!mediaStream) return;

    facingMode = facingMode === 'user' ? 'environment' : 'user';

    try {
      mediaStream.getVideoTracks().forEach(track => track.stop());

      const newStream = await getMediaStream(facingMode);
      const newVideoTrack = newStream.getVideoTracks()[0];

      const oldVideoTrack = mediaStream.getVideoTracks()[0];
      if (oldVideoTrack) {
        mediaStream.removeTrack(oldVideoTrack);
      }
      mediaStream.addTrack(newVideoTrack);

      preview.srcObject = mediaStream;
      preview.style.transform = facingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)';

      newStream.getAudioTracks().forEach(track => track.stop());

      // If streaming, need to update the peer connection
      if (isStreaming && streamer) {
        // The peer connection will automatically use the new track
        // since we're replacing it in the same MediaStream
      }
    } catch (error) {
      console.error('Failed to flip camera:', error);
      facingMode = facingMode === 'user' ? 'environment' : 'user';
    }
  }

  async function startStreaming(): Promise<void> {
    if (!mediaStream) return;

    try {
      updateStatus('connecting');
      setupControls.style.display = 'none';
      streamControls.style.display = 'flex';
      isStreaming = true;

      streamer = new StreamerConnection(eventId, mediaStream, {
        onConnected: () => {
          updateStatus('streaming');
        },
        onDisconnected: () => {
          updateStatus('disconnected');
        },
        onReconnecting: () => {
          updateStatus('reconnecting');
        },
        onError: (error) => {
          console.error('Streamer error:', error);
          showError(error.message);
        },
      });

      streamer.connect();
    } catch (error: any) {
      showError(error.message || 'Failed to start streaming');
    }
  }

  function stopStreaming(): void {
    isStreaming = false;
    streamer?.disconnect();
    mediaStream?.getTracks().forEach(track => track.stop());
    preview.srcObject = null;
    mediaStream = null;
    updateStatus('disconnected');
    streamControls.style.display = 'none';
    setupControls.style.display = 'none';

    // Reinitialize camera for setup
    initializeCamera();
  }

  // Event handlers
  flipCameraSetupBtn.addEventListener('click', flipCamera);
  startStreamingBtn.addEventListener('click', startStreaming);

  retryBtn.addEventListener('click', () => {
    errorMessage.style.display = 'none';
    initializeCamera();
  });

  flipCameraBtn.addEventListener('click', flipCamera);

  cameraToggle.addEventListener('click', () => {
    if (!mediaStream) return;
    cameraEnabled = !cameraEnabled;
    mediaStream.getVideoTracks().forEach(track => {
      track.enabled = cameraEnabled;
    });
    cameraToggle.textContent = cameraEnabled ? '📹' : '📷';
    cameraToggle.classList.toggle('disabled', !cameraEnabled);
  });

  micToggle.addEventListener('click', () => {
    if (!mediaStream) return;
    micEnabled = !micEnabled;
    mediaStream.getAudioTracks().forEach(track => {
      track.enabled = micEnabled;
    });
    micToggle.textContent = micEnabled ? '🎤' : '🔇';
    micToggle.classList.toggle('disabled', !micEnabled);
  });

  stopBtn.addEventListener('click', () => {
    if (confirm('Stop streaming?')) {
      stopStreaming();
    }
  });

  // Cleanup on navigation
  window.addEventListener('beforeunload', () => {
    streamer?.disconnect();
    mediaStream?.getTracks().forEach(track => track.stop());
  });

  // Auto-initialize camera on page load
  initializeCamera();
}
